import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/shared/lib/cn'

interface AuroraBackgroundProps {
  className?: string
  /** Intensity of the blobs (0–1). Default 0.6. */
  intensity?: number
}

/**
 * Aurora-style mesh-gradient background (§6.7 + ui-ux-pro-max "Aurora UI").
 *
 * Three large blurred radial gradients morph slowly across the surface
 * (8–12s per loop, the skill's prescribed range). Pure CSS-driven —
 * `transform/opacity` only, GPU-cheap, mobile-safe.
 *
 * The component also tracks pointer X/Y (via CSS custom props) so the blobs
 * gently follow the cursor on desktop. `prefers-reduced-motion` users get
 * a static gradient and the pointer tracking is skipped.
 */
export function AuroraBackground({
  className,
  intensity = 0.6,
}: AuroraBackgroundProps) {
  const ref = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    if (reducedMotion) return
    const el = ref.current
    if (!el) return
    let raf = 0
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect()
        const x = ((e.clientX - r.left) / r.width) * 100
        const y = ((e.clientY - r.top) / r.height) * 100
        el.style.setProperty('--mx', `${x}%`)
        el.style.setProperty('--my', `${y}%`)
      })
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [reducedMotion])

  return (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 overflow-hidden',
        className,
      )}
      style={
        {
          '--mx': '50%',
          '--my': '40%',
          opacity: intensity,
        } as React.CSSProperties
      }
    >
      {/* Blob 1 — primary orange, follows pointer.
          Uses `translate3d` (not `left/top`) so pointer movement
          composites on the GPU and never contributes to CLS. */}
      <div
        className={cn(
          'absolute left-0 top-0 h-[60vmax] w-[60vmax] rounded-full',
          'bg-[radial-gradient(circle,_hsl(var(--primary)/0.55)_0%,_transparent_60%)]',
          'blur-3xl will-change-transform',
          !reducedMotion && 'animate-aurora-1',
        )}
        style={{
          transform:
            'translate3d(calc(var(--mx) - 30vmax), calc(var(--my) - 30vmax), 0)',
        }}
      />
      {/* Blob 2 — peach accent, slow morph */}
      <div
        className={cn(
          'absolute -end-[20vmax] top-[10%] h-[50vmax] w-[50vmax] rounded-full',
          'bg-[radial-gradient(circle,_hsl(var(--accent)/0.45)_0%,_transparent_60%)]',
          'blur-3xl will-change-transform',
          !reducedMotion && 'animate-aurora-2',
        )}
      />
      {/* Blob 3 — deep amber bottom-start, slowest */}
      <div
        className={cn(
          'absolute -start-[20vmax] bottom-[-15vmax] h-[55vmax] w-[55vmax] rounded-full',
          'bg-[radial-gradient(circle,_hsl(15_90%_55%/0.35)_0%,_transparent_65%)]',
          'blur-3xl will-change-transform',
          !reducedMotion && 'animate-aurora-3',
        )}
      />
      {/* Subtle noise to break gradient banding (§6.7) */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay bg-noise"
      />
    </div>
  )
}
